/**
 * Client pop-ups (guide §9 layout, ~560 px, a bottom sheet on a phone):
 *
 * - EditPriorityModal — prototype `editClient()`: the three priority options with what each means, the
 *   view's suggestion with its reasons and a "Use …" link, annual revenue, strategic flag and the note the
 *   team sees. GET /clients/{id} → PATCH /clients/{id} (If-Match). Capability `clients.edit.all`.
 * - AddClientModal — POST /clients (capability `clients.create`) with an Idempotency-Key. `spoc_id` is
 *   required: a client without a SPOC is visible to no member.
 */
import { useMemo, useState } from 'react';
import {
  ROLE_LABEL,
  SPOC_ELIGIBLE_ROLES,
  type ClientDetail,
  type CreateClientBody,
  type Priority,
  type Role,
  type Segment,
  type UpdateClientBody,
} from '@amplispot/contracts';
import { ApiFailure } from '../../api';
import { newIdempotencyKey, useClient, useCreateClient, useUpdateClient, useUsers } from '../../lib/data';
import { firstName, todayIso } from '../../lib/format';
import { ErrorState, Field, Loading, Modal, errorText, priorityLabel, useToast } from '../../ui/kit';

const PRIORITIES: Priority[] = ['high', 'med', 'low'];
const SEGMENTS: Segment[] = ['BFSI', 'QSR', 'Retail'];
const PRIO_HINT: Record<Priority, string> = {
  high: 'Shown first, gets the most attention',
  med: 'Normal service',
  low: 'Looked at after the others',
};

function PriorityOptions({ value, onChange }: { value: Priority; onChange: (p: Priority) => void }) {
  return (
    <div className="popts" role="radiogroup" aria-label="Priority">
      {PRIORITIES.map((p) => (
        <button key={p} type="button" role="radio" aria-checked={value === p} className={`popt${value === p ? ' on' : ''}`} onClick={() => onChange(p)}>
          <span className={`pc pc-${p}`}>
            <span className="pbars">
              {[1, 2, 3].map((i) => (
                <i key={i} className={i <= { high: 3, med: 2, low: 1 }[p] ? 'f' : ''} />
              ))}
            </span>
            {priorityLabel(p)}
          </span>
          <small>{PRIO_HINT[p]}</small>
        </button>
      ))}
    </div>
  );
}

/** Revenue input → number | null | 'bad'. Empty means "not set". */
function parseLakh(v: string): number | null | 'bad' {
  if (!v.trim()) return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : 'bad';
}

// ---------------------------------------------------------------- edit priority and revenue

export function EditPriorityModal({ clientId, onClose }: { clientId: string; onClose: () => void }) {
  const detail = useClient(clientId);
  if (detail.isPending || detail.isError)
    return (
      <Modal title="Priority and revenue" onClose={onClose} footer={<><div className="grow" /><button className="btn" onClick={onClose}>Close</button></>}>
        {detail.isPending ? <Loading label="Loading the client…" /> : <ErrorState error={detail.error} onRetry={() => detail.refetch()} />}
      </Modal>
    );
  return <EditPriorityForm key={clientId} loaded={detail.data} onClose={onClose} />;
}

type Detail = ClientDetail;

function EditPriorityForm({ loaded, onClose }: { loaded: Detail; onClose: () => void }) {
  // Frozen at the moment the form opened: the form, its diff and the If-Match version all come from this one
  // snapshot. A background refetch must neither wipe what the manager is typing nor swap in a newer version
  // (which would let the save overwrite someone else's change); a stale save gets the 412 message instead.
  const [client] = useState(loaded);
  const update = useUpdateClient();
  const toast = useToast();
  const hasRevenue = 'annual_revenue_lakh' in client;
  const [prio, setPrio] = useState<Priority>(client.priority);
  const [rev, setRev] = useState(client.annual_revenue_lakh == null ? '' : String(client.annual_revenue_lakh));
  const [strategic, setStrategic] = useState(client.strategic);
  const [note, setNote] = useState(client.priority_note ?? '');
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [formErr, setFormErr] = useState<string | null>(null);
  const sg = client.priority_suggestion;

  const save = () => {
    const body: UpdateClientBody = {};
    const local: Record<string, string> = {};
    if (prio !== client.priority) body.priority = prio;
    if (hasRevenue) {
      const r = parseLakh(rev);
      if (r === 'bad') local.annual_revenue_lakh = 'Enter the amount in lakh as a number, for example 96';
      else if (r !== (client.annual_revenue_lakh ?? null)) body.annual_revenue_lakh = r;
    }
    if (strategic !== client.strategic) body.strategic = strategic;
    const n = note.trim();
    if (n !== (client.priority_note ?? '')) body.priority_note = n ? n : null;
    setErrs(local);
    setFormErr(null);
    if (Object.keys(local).length) return;
    if (!Object.keys(body).length) return onClose();
    update.mutate(
      { id: client.id, version: client.version, body },
      {
        onSuccess: () => {
          toast(body.priority ? `${client.name} is now ${priorityLabel(body.priority).toLowerCase()} priority` : `${client.name} saved`);
          onClose();
        },
        onError: (e) => {
          if (e instanceof ApiFailure) setErrs(e.fieldErrors);
          setFormErr(
            e instanceof ApiFailure && e.status === 412
              ? `Someone else changed ${client.name} a moment ago. Close this and open it again to see their change.`
              : errorText(e),
          );
        },
      },
    );
  };

  return (
    <Modal
      title={client.name}
      sub="Priority decides where this client sits on Today and in every list. The team sees the priority and the reasons, never the revenue figure."
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={save} disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {formErr ? <div className="note bad">{formErr}</div> : null}
      <div className="lbl" style={{ marginBottom: 8 }}>
        Priority
      </div>
      <PriorityOptions value={prio} onChange={setPrio} />
      <div className="note" style={{ margin: '12px 0 14px' }}>
        <b>Suggested: {priorityLabel(sg.suggested)}.</b> {sg.reasons.length ? sg.reasons.join('. ') + '.' : 'No special reason, standard service.'}
        {prio !== sg.suggested ? (
          <>
            {' '}
            <button type="button" className="lnk" onClick={() => setPrio(sg.suggested)}>
              Use {priorityLabel(sg.suggested)}
            </button>
          </>
        ) : null}
      </div>
      <div className="cols c2">
        {hasRevenue ? (
          <Field label="Annual revenue (₹ lakh) · managers only" error={errs.annual_revenue_lakh}>
            <input className="in" type="number" min={0} step="any" inputMode="decimal" value={rev} onChange={(e) => setRev(e.target.value)} aria-invalid={!!errs.annual_revenue_lakh || undefined} />
          </Field>
        ) : null}
        <Field label="Strategic account" error={errs.strategic}>
          <select className="in" value={strategic ? '1' : '0'} onChange={(e) => setStrategic(e.target.value === '1')}>
            <option value="0">No</option>
            <option value="1">Yes, treat as top priority</option>
          </select>
        </Field>
      </div>
      <Field label="Note for the team (they will see this)" error={errs.priority_note}>
        <input className="in" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Renewal in December, keep the CX head happy" />
      </Field>
    </Modal>
  );
}

// ---------------------------------------------------------------- add a client

export function AddClientModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const users = useUsers();
  const create = useCreateClient();
  const toast = useToast();
  const [key] = useState(newIdempotencyKey);
  const [name, setName] = useState('');
  const [segment, setSegment] = useState<Segment | ''>('');
  const [since, setSince] = useState(todayIso);
  const [spocId, setSpocId] = useState('');
  const [hours, setHours] = useState('');
  const [prio, setPrio] = useState<Priority>('med');
  const [rev, setRev] = useState('');
  const [strategic, setStrategic] = useState(false);
  const [note, setNote] = useState('');
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [formErr, setFormErr] = useState<string | null>(null);

  const eligible = useMemo(
    () =>
      (users.data ?? [])
        .filter((u) => u.status === 'active' && (SPOC_ELIGIBLE_ROLES as readonly Role[]).includes(u.role))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [users.data],
  );

  const submit = () => {
    const local: Record<string, string> = {};
    if (!name.trim()) local.name = 'Enter the client’s name';
    if (!segment) local.segment = 'Pick the segment';
    if (!since) local.since = 'Enter the date they became a client';
    if (!spocId) local.spoc_id = 'Pick who looks after this client';
    const h = Number(hours);
    if (!hours.trim() || !Number.isFinite(h) || h < 0 || h > 744) local.monthly_hours = 'Enter the monthly hours, from 0 to 744';
    const r = parseLakh(rev);
    if (r === 'bad') local.annual_revenue_lakh = 'Enter the amount in lakh as a number, for example 96';
    setErrs(local);
    setFormErr(null);
    if (Object.keys(local).length) return;

    const body: CreateClientBody = {
      name: name.trim(),
      segment: segment as Segment,
      since,
      monthly_hours: h,
      priority: prio,
      spoc_id: spocId,
      ...(r !== null && r !== 'bad' ? { annual_revenue_lakh: r } : {}),
      ...(strategic ? { strategic: true } : {}),
      ...(note.trim() ? { priority_note: note.trim() } : {}),
    };
    create.mutate(
      { body, key },
      {
        onSuccess: (c) => {
          toast(`${c.name} added. ${firstName(c.spoc.name)} is the SPOC.`);
          onCreated(c.id);
        },
        onError: (e) => {
          if (e instanceof ApiFailure) setErrs(e.fieldErrors);
          setFormErr(errorText(e));
        },
      },
    );
  };

  return (
    <Modal
      title="Add a client"
      sub="The SPOC sees this client straight away. Priority and revenue can be changed later."
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Adding…' : 'Add client'}
          </button>
        </>
      }
    >
      {formErr ? <div className="note bad">{formErr}</div> : null}
      <Field label="Client name" error={errs.name}>
        <input className="in" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} placeholder="e.g. Kestrel Finance" aria-invalid={!!errs.name || undefined} autoFocus />
      </Field>
      <div className="cols c2">
        <Field label="Segment" error={errs.segment}>
          <select className="in" value={segment} onChange={(e) => setSegment(e.target.value as Segment)} aria-invalid={!!errs.segment || undefined}>
            <option value="">Choose a segment</option>
            {SEGMENTS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Client since" error={errs.since}>
          <input className="in" type="date" value={since} onChange={(e) => setSince(e.target.value)} aria-invalid={!!errs.since || undefined} />
        </Field>
      </div>
      <div className="cols c2">
        <Field label="SPOC" error={errs.spoc_id ?? (users.isError ? errorText(users.error) : undefined)}>
          <select className="in" value={spocId} onChange={(e) => setSpocId(e.target.value)} aria-invalid={!!errs.spoc_id || undefined} disabled={users.isPending}>
            <option value="">{users.isPending ? 'Loading people…' : eligible.length ? 'Choose a person' : 'No one can hold a client yet'}</option>
            {eligible.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} · {ROLE_LABEL[u.role]} · {u.client_count} client{u.client_count === 1 ? '' : 's'}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Ops hours a month" error={errs.monthly_hours}>
          <input className="in" type="number" min={0} max={744} step="any" inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="e.g. 40" aria-invalid={!!errs.monthly_hours || undefined} />
        </Field>
      </div>
      <div className="lbl" style={{ marginBottom: 8 }}>
        Priority
      </div>
      <PriorityOptions value={prio} onChange={setPrio} />
      <div className="cols c2" style={{ marginTop: 14 }}>
        <Field label="Annual revenue (₹ lakh) · managers only" error={errs.annual_revenue_lakh} hint="Leave empty if not known yet.">
          <input className="in" type="number" min={0} step="any" inputMode="decimal" value={rev} onChange={(e) => setRev(e.target.value)} aria-invalid={!!errs.annual_revenue_lakh || undefined} />
        </Field>
        <Field label="Strategic account">
          <select className="in" value={strategic ? '1' : '0'} onChange={(e) => setStrategic(e.target.value === '1')}>
            <option value="0">No</option>
            <option value="1">Yes, treat as top priority</option>
          </select>
        </Field>
      </div>
      <Field label="Note for the team (they will see this)" error={errs.priority_note}>
        <input className="in" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Renewal in December, keep the CX head happy" />
      </Field>
    </Modal>
  );
}
